import { useEffect, useRef } from "react";
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  createSeriesMarkers,
  type IChartApi,
  type UTCTimestamp,
} from "lightweight-charts";
import type { Bar } from "../api/client";

// Lightweight-charts v5 wrapper: candlesticks in pane 0 (price axis),
// indicators in pane 1 (independent axis — "normalize separately"). The
// underlying lib is imperative — we own one chart instance per mount via
// useEffect and tear it down on unmount.

export interface IndicatorLine {
  key: string;
  color: string;
  /** values[i] aligns 1:1 with bars[i]. nulls become whitespace. */
  values: (number | null)[];
  /**
   * Which lightweight-charts pane to draw this line in. Default 1 (the first
   * indicator pane below the candles). Use higher numbers to split indicators
   * with different magnitude ranges (e.g. realized volatility 0..0.05 vs
   * returns ±0.001) so auto-scaling doesn't flatten the smaller series.
   */
  paneIndex?: number;
}

/** One pinpoint marker on the price pane. Pass an array of these for the
 * day view (one per (decision_ts, model_id) so stray models pop out), or
 * a single-element array for the per-decision replay view. */
export interface PriceMarker {
  time: string;        // ISO timestamp
  price?: number | null;
  color: string;       // hex/rgb — caller decides (by-direction, by-model, etc.)
  text?: string;
  shape?: "circle" | "square" | "arrowUp" | "arrowDown";
  position?: "aboveBar" | "belowBar" | "inBar";
  /** lightweight-charts marker size multiplier. Default 1; 2-3 is much
   *  more visible on a dense candle chart (e.g. day view with 70+ bars). */
  size?: number;
}

export interface CandleChartProps {
  bars: Bar[];
  indicators: IndicatorLine[];
  /** Arbitrary list of markers on the candle pane. Empty/undefined = no markers. */
  markers?: PriceMarker[];
  height?: number;
}

function toUtcSeconds(iso: string): UTCTimestamp {
  // Backend convention is UTC for every timestamp, but some rows (notably
  // ModelPredictions.decision_ts) are persisted without a timezone suffix
  // — e.g. "2026-05-19T14:00:00" instead of "2026-05-19T14:00:00+00:00".
  // Without a suffix, `new Date(...)` parses as the browser's local zone,
  // which silently shifts markers by the user's UTC offset (showed up as a
  // 2-hour decision/next gap for a CEST user). Coerce naked ISO strings to
  // UTC by appending "Z" before parsing.
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const normalized = hasTz ? iso : `${iso}Z`;
  return Math.floor(new Date(normalized).getTime() / 1000) as UTCTimestamp;
}

export function CandleChart({
  bars,
  indicators,
  markers,
  height = 480,
}: CandleChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      height,
      width: container.clientWidth,
      layout: {
        background: { color: "#0b1220" },
        textColor: "#cbd5e1",
        // 60% candle pane, 40% indicator pane. lightweight-charts auto-sizes
        // panes equally otherwise — this gives candles more room.
        panes: { separatorColor: "#1e293b", separatorHoverColor: "#475569", enableResize: true },
      },
      grid: {
        vertLines: { color: "#1e293b" },
        horzLines: { color: "#1e293b" },
      },
      rightPriceScale: { borderColor: "#334155" },
      timeScale: {
        borderColor: "#334155",
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        vertLine: { color: "#475569", width: 1, style: 3 },
        horzLine: { color: "#475569", width: 1, style: 3 },
      },
    });
    chartRef.current = chart;

    // Pane 0: candlesticks
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#22c55e",
      downColor: "#ef4444",
      borderUpColor: "#22c55e",
      borderDownColor: "#ef4444",
      wickUpColor: "#22c55e",
      wickDownColor: "#ef4444",
    });
    candleSeries.setData(
      bars.map((b) => ({
        time: toUtcSeconds(b.t),
        open: b.o,
        high: b.h,
        low: b.l,
        close: b.c,
      })),
    );

    // Each indicator gets its own line series. paneIndex defaults to 1
    // (the first sub-pane below the candles). Callers can route series with
    // mismatched magnitude ranges (e.g. realized volatility) to a separate
    // pane so they don't dominate the shared auto-scaled axis.
    for (const ind of indicators) {
      const targetPane = ind.paneIndex ?? 1;
      const series = chart.addSeries(
        LineSeries,
        {
          color: ind.color,
          lineWidth: 2,
          title: ind.key,
          priceLineVisible: false,
          lastValueVisible: false,
        },
        targetPane,
      );
      series.setData(
        bars
          .map((b, i) => {
            const v = ind.values[i];
            if (v === null || v === undefined || !Number.isFinite(v)) return null;
            return { time: toUtcSeconds(b.t), value: v };
          })
          .filter((p): p is { time: UTCTimestamp; value: number } => p !== null),
      );
    }

    // Caller-supplied markers (single decision in the replay view, many
    // dots — one per model — in the day view). lightweight-charts requires
    // markers sorted ascending by time.
    if (markers && markers.length > 0) {
      const lwMarkers = [...markers]
        .map((m) => ({
          time: toUtcSeconds(m.time),
          position: m.position ?? "aboveBar",
          color: m.color,
          shape: m.shape ?? "circle",
          text: m.text,
          size: m.size ?? 1,
        }))
        .sort((a, b) => (a.time as number) - (b.time as number));
      createSeriesMarkers(candleSeries, lwMarkers);
    }

    chart.timeScale().fitContent();

    // Resize on container width change
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        chart.applyOptions({ width: w });
      }
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
    };
  }, [bars, indicators, markers, height]);

  return <div ref={containerRef} style={{ width: "100%", height: `${height}px` }} />;
}
